import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OrderDetailModel } from 'src/models/orders/orderDetails/orderDetail.model';
import { OrderAddress } from 'src/models/orders/orderAddresses/order.address.model';
import { OrderItemModel } from 'src/models/orders/orderItems/orderItems.model';
import { SalesChannel } from 'src/models/orders/sales.channel.enum';

@Injectable({
	providedIn: 'root'
})
export class OrderFormService {

	constructor(private fb: UntypedFormBuilder) {
	}

	createOrderEditForm(entry: OrderDetailModel): UntypedFormGroup {
		// Everything else is not editable via the edit-component (separate modals / buttons or just not editable)
		return this.fb.group({
			billingAddress: this.createAddressForm(entry.billingAddress),
			shippingAddress: this.createAddressForm(entry.shippingAddress),
			remarks: [entry.remarks]
		});
	}

	private createAddressForm(address: OrderAddress): any {
		return this.fb.group({
			name: [address.name],
			firstname: [address.firstname],
			lastname: [address.lastname],
			name2: [address.name2],
			name3: [address.name3],
			street: [address.street],
			postalCode: [address.postalCode],
			city: [address.city],
			country: [address.country],
			countryCode: [address.countryCode],
			phoneNumber: [address.phoneNumber],
			mailAddress: [address.mailAddress],
			vatId: [address.vatId],
		});
	}

	createOrderConfirmForm(orderDetails: OrderDetailModel): UntypedFormGroup {
		const articleFormGroups = orderDetails.orderItems.map((orderItem: OrderItemModel) => {
			const group = this.fb.group({
				description: [{value: orderItem.description, disabled: true}],
				orderedAmount: [{value: orderItem.orderedAmount, disabled: true}],
				sku: [{value: orderItem.sku, disabled: true}],
				comment: [null],
				quantity: [{value: orderItem.orderedAmount, disabled: true}],
				uuid: [orderItem.id]
			});
			// Leider notwendig. Nur Egis hat Positionsspezifisches Ablehnen - Conrad hingegen nur "ja oder nein"
			if (orderDetails.salesChannel === SalesChannel.Egis) {
				const quantField = group.get('quantity');
				if (!quantField) {
					throw Error('could not find quantity field');
				}
				quantField.setValidators([Validators.required, Validators.min(0), Validators.max(orderItem.orderedAmount)]);
				quantField.enable();
			}

			return group;
		});

		return this.fb.group({
			acceptedItems: this.fb.array(articleFormGroups),
			rejectedItems: this.fb.array([])
		});
	}

	private createOrderItemsForm(orderItems: OrderItemModel[]): UntypedFormArray {
		const itemGroups = orderItems.map((item: OrderItemModel) => {
			return this.fb.group({
				id: [item.id],
				sku: [item.sku],
				description: [item.description],
				orderedAmount: [item.orderedAmount, [Validators.required, Validators.min(1)]],
				singlePriceNet: [item.singlePriceNet, Validators.required],
				singlePriceGross: [item.singlePriceGross, Validators.required],
				totalPriceNet: [item.totalPriceNet],
				totalPriceGross: [item.totalPriceGross],
				tax: [item.tax],
				quantityConfirmed: [item.quantityConfirmed],
				comment: [item.orderResponseComment]
			});
		});

		return this.fb.array(itemGroups);
	}

	createOrderItemEditForm(order: OrderDetailModel): UntypedFormGroup {
		return this.fb.group({
			orderItems: this.createOrderItemsForm(order.orderItems)
		});
	}

	getOrderCommentForm(orderDetails: OrderDetailModel): UntypedFormGroup {
		return this.fb.group({
			remarks: [orderDetails.remarks || ''],
		});
	}

	getPaymentMethodEditForm(orderDetails: OrderDetailModel): UntypedFormGroup {
		return this.fb.group({
			paymentMethod: [orderDetails.paymentMethod || ''],
		});
	}
}
